/**
 * Tools runtime (M2-15; plan §2.3, §2.7; spec §4, §5.2, §6.3 step 1): the player's tool modes, laser power, nav
 * targeting, scans, the dock key and player death detection.
 *
 * - **control stage** (after the AI step): the first tick of a frame consumes the edge fields of `input` and clears
 *   them (`clearToolInputEdges`), so later ticks of the same frame see none. Mode edges (Backquote / pad R3 tap cycles
 *   Combat → Mining → Salvage, M / B pick one), power steps (Mining mode, × `fracture.powerStep`, clamped to the fitted laser's max)
 *   and the smoothed power; the player's `ToolControl` (mode, fire = the M1 `Control.firePrimary`, alt =
 *   `Control.fireSecondary`, power), and outside Combat mode the M1 triggers are zeroed so no gun or torpedo fires
 *   (D-TBD: tools are ship systems driven by `ToolControl`); the target key in Mining / Salvage mode picks a rock or
 *   a wreck; the dock key picks a station and requests clearance, or starts auto-dock when cleared and close; the
 *   scan (R / pad X held on the nav target within scanner range for `scan.holdSeconds`) fills the scan readout.
 *   Any change of the nav target (a pick, a dock press, or a runtime clearing a dead target) resets the scan and
 *   emits `navTarget`. R held with no target is M3's deep-scan ping and is left alone here.
 * - **deaths stage**: the player's `Life.alive` dropping to 0 this tick → `playerDied` (killer and position from
 *   this tick's M1 `kill` event) and auto-dock cancelled; `docking.end` releases the clearance later in the tick.
 *
 * Allocation-free per tick (D-014): the stage methods are closures bound once at creation; everything they run
 * uses the runtime state and module scratch. Positions are float64 world metres (D-011).
 */
import { EV, eventSlot, firstReadable } from '../combat/events'
import { SIM_DT } from '../core/loop'
import type { Vec3 } from '../core/math'
import type { Sim } from '../sim'
import { Control, Life, Pos64, Rot } from '../world/components'
import { ToolControl } from './components'
import {
  NAV,
  TOOL,
  TOOL_COUNT,
  clearNav,
  clearToolInputEdges,
  createNavTarget,
  createScanReadout,
  sameNav,
  setNav,
  type DockingRuntime,
  type NavKind,
  type NavTarget,
  type ScanReadout,
  type SpaceContext,
  type StationRuntime,
  type ToolMode,
  type ToolsDeps,
  type ToolsRuntime,
} from './contracts'
import { SEV, emitSpaceEvent, firstReadableSpace, spaceEventSlot } from './events'

/**
 * Picking cones (spec §4; plan M2-15). Mutable so the dev panel can bind them (the M1 `TARGETING` pattern).
 * D-TBD: move into mining.json (`scan.navConeDeg`) and docking.json (`pickConeDeg`) when those schemas are next owned.
 */
export const TOOL_TUNING = {
  /** Half-angle (radians) around the reticle for the rock and wreck pick: 20°. Outside it, the nearest within scanner range. */
  navCone: (20 * Math.PI) / 180,
  /** Half-angle (radians) around the reticle for the station pick of the dock key: 30°. Outside it, the nearest within `requestRange`. */
  dockCone: (30 * Math.PI) / 180,
}

/** Scan progress at or above this counts as a full hold (60 ticks of 1/60 s sum to 1 only within rounding). */
const SCAN_DONE = 1 - 1e-9

interface ToolsState {
  readonly ctx: SpaceContext
  readonly deps: ToolsDeps
  readonly docking: DockingRuntime
  readonly scan: ScanReadout
  /** The nav target the scan and the last `navTarget` event refer to. */
  readonly lastNav: NavTarget
  /** The current scan completed during this hold (no repeat until R is released). */
  scanLatched: boolean
  /** Player eid and whether it was alive at this tick's control stage. */
  controlEid: number
  aliveAtControl: boolean
  /** Combat ring head at the end of the last deaths stage: the player's `kill` event, if any, is at or after it. */
  killScanFrom: number
  /** Space ring head after the last control stage, and the id of the station the player last undocked or unmoored from ('' none; M10 P1 W4a). */
  evFrom: number
  leftId: string
}

// Module scratch (D-014).
const origin: Vec3 = { x: 0, y: 0, z: 0 }
const nose: Vec3 = { x: 0, y: 0, z: -1 }
const target: Vec3 = { x: 0, y: 0, z: 0 }
const pick: NavTarget = createNavTarget()

/** Ship `eid`'s position (float64) and nose (local −Z, world unit vector) into the module scratch. */
function loadRay(eid: number): void {
  origin.x = Pos64.x[eid]
  origin.y = Pos64.y[eid]
  origin.z = Pos64.z[eid]
  const qx = Rot.x[eid]
  const qy = Rot.y[eid]
  const qz = Rot.z[eid]
  const qw = Rot.w[eid]
  nose.x = -2 * (qx * qz + qw * qy)
  nose.y = -2 * (qy * qz - qw * qx)
  nose.z = -(1 - 2 * (qx * qx + qy * qy))
}

/** Distance from the player to a station's hull: centre distance less its radius (scale build §3.3; may be < 0 inside). */
function hullDistance(s: Readonly<StationRuntime>): number {
  return distanceFromOrigin(s.pos) - s.radius
}

function distanceFromOrigin(p: Readonly<Vec3>): number {
  const dx = p.x - origin.x
  const dy = p.y - origin.y
  const dz = p.z - origin.z
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** Forgets the current scan (a new target): progress 0, no result, nothing scanned. */
function resetScan(st: ToolsState): void {
  const player = st.ctx.player
  const scan = st.scan
  player.scanProgress = 0
  clearNav(player.scanned)
  scan.progress = 0
  scan.complete = false
  scan.kind = player.nav.kind
  scan.index = player.nav.index
  scan.rock.valid = false
  scan.wreck.valid = false
  st.scanLatched = false
}

/** Switches the player's tool mode: emits `toolMode` and restarts any scan hold. False when unchanged or invalid. */
function applyMode(st: ToolsState, mode: number, eid: number): boolean {
  const player = st.ctx.player
  if (mode < 0 || mode >= TOOL_COUNT || mode === player.mode) return false
  player.mode = mode as ToolMode
  player.scanProgress = 0
  st.scan.progress = 0
  st.scanLatched = false
  emitSpaceEvent(st.ctx.events, SEV.toolMode, eid, mode, 0, 0, 0, 0, 0, 0, 0)
  return true
}

/** A nav target changed by anyone since the last tick (pick, dock press, a runtime clearing a dead target): reset the scan, emit `navTarget`. */
function syncNav(st: ToolsState, eid: number): void {
  const nav = st.ctx.player.nav
  if (sameNav(nav, st.lastNav)) return
  setNav(st.lastNav, nav.kind, nav.index)
  resetScan(st)
  emitSpaceEvent(st.ctx.events, SEV.navTarget, eid, nav.kind, nav.index, 0, 0, 0, 0, 0, 0)
}

/** The target key in a tool mode: a rock (Mining) or wreck (Salvage) nearest the reticle, else the nearest in scanner range. */
function pickTarget(st: ToolsState): void {
  const player = st.ctx.player
  const range = st.ctx.mirror.tools.scannerRange
  let found: boolean
  if (player.mode === TOOL.mining) {
    found = st.deps.rocks.pick(origin.x, origin.y, origin.z, nose.x, nose.y, nose.z, TOOL_TUNING.navCone, range, pick)
  } else if (player.mode === TOOL.salvage) {
    found = st.deps.salvage.pick(origin.x, origin.y, origin.z, nose.x, nose.y, nose.z, TOOL_TUNING.navCone, range, pick)
  } else {
    // Combat mode: the M1 targeting owns the key.
    return
  }
  if (found) setNav(player.nav, pick.kind, pick.index)
  else clearNav(player.nav)
  // A boom hull (P5a12c, Q11): the same key marks the picked rock for the booms, or unmarks it (up to four marks,
  // inside booms.rangeM). S/M hulls (no booms) keep exactly the pick above.
  const rocks = st.deps.rocks
  if (found && player.mode === TOOL.mining && rocks.boomCount !== undefined && rocks.boomCount() > 0) {
    rocks.toggleMark?.(pick.kind, pick.index)
  }
}

/** Reads this tick's `undocked` events for the player: the station launched from (bay or mooring ring) becomes `leftId`. */
function trackLeft(st: ToolsState, eid: number): void {
  const ring = st.ctx.events
  const list = st.deps.stations.list
  for (let i = firstReadableSpace(ring, st.evFrom); i < ring.head; i++) {
    const k = spaceEventSlot(i)
    if (ring.type[k] === SEV.undocked && ring.eid[k] === eid && ring.a[k] >= 0 && ring.a[k] < list.length) st.leftId = list[ring.a[k]].def.id
  }
  st.evFrom = ring.head
}

/** Whether station `s`'s hull is within the range a dock request can be granted from for the player's hull (moor or bay). */
function inRequestRange(st: ToolsState, s: number): boolean {
  return hullDistance(st.deps.stations.list[s]) <= st.docking.requestRange(s)
}

/** Index of the station the player last left while it is within its request range, else -1 (matched by id, so a jump drops it). */
function leftInRange(st: ToolsState): number {
  if (st.leftId === '') return -1
  const list = st.deps.stations.list
  for (let i = 0; i < list.length; i++) if (list[i].def.id === st.leftId) return inRequestRange(st, i) ? i : -1
  return -1
}

/**
 * The dock key: the station nearest the reticle within `dockCone` (any range, so a far station still answers "too
 * far"), else the nearest within its request range (every range here is from the station's hull, scale build §3.3). The station last undocked or unmoored from, while within
 * its request range, beats a reticle pick beyond the pick's (W4a: a ship launched from a mooring ring faces away). The request range is
 * the one `docking.request` grants within for the player's hull at that station (`DockingRuntime.requestRange`): `moor.rangeM` for a
 * hull that moors there, else `docking.requestRange`; checking a moor hull's pick against the bay range let a km-scale station 3–5 km
 * off its hull beat the station just left and answer "out of range" (SC-3f, moor.spec). Cleared for it and within `autodockRange` → auto-dock; otherwise a
 * clearance request. Ignored while auto-docking or docked.
 */
function pressDock(st: ToolsState): void {
  const docking = st.docking
  const readout = docking.readout
  // M10 P5a9b2: near an own holding L with a cradle the dock key asks the cradle (or launches from it); else as before.
  if (docking.cradleKey?.() === true) return
  if (readout.phase === 'autodock' || readout.phase === 'docked') return
  const stations = st.deps.stations
  const cfg = st.ctx.data.docking
  let s = stations.pick(origin.x, origin.y, origin.z, nose.x, nose.y, nose.z, TOOL_TUNING.dockCone, Number.POSITIVE_INFINITY)
  if (s < 0 || !inRequestRange(st, s)) {
    const left = leftInRange(st)
    if (left >= 0) s = left
  }
  if (s < 0) {
    const n = stations.nearest(origin.x, origin.y, origin.z)
    if (n >= 0 && inRequestRange(st, n)) s = n
  }
  if (s < 0) return
  setNav(st.ctx.player.nav, NAV.station, s)
  if (readout.phase === 'cleared' && readout.station === s && hullDistance(stations.list[s]) <= cfg.autodockRange) {
    docking.startAutodock()
  } else {
    docking.request(s)
  }
}

function isScannable(kind: NavKind): boolean {
  return kind === NAV.rock || kind === NAV.fragment || kind === NAV.wreck
}

/** True when the scannable nav target exists and its surface is within scanner range of the player (origin loaded). */
function scanTargetInRange(st: ToolsState, kind: NavKind, index: number): boolean {
  let radius: number
  if (kind === NAV.wreck) {
    const w = st.deps.salvage.wrecks
    if (index < 0 || index >= w.capacity || w.alive[index] !== 1) return false
    target.x = w.x[index]
    target.y = w.y[index]
    target.z = w.z[index]
    radius = w.radius[index]
  } else {
    const rocks = st.deps.rocks
    if (!rocks.position(kind, index, target)) return false
    radius = rocks.radius(kind, index)
  }
  return distanceFromOrigin(target) - radius <= st.ctx.mirror.tools.scannerRange
}

/** One tick of the scan hold on the nav target (Mining and Salvage modes; a target change already reset it). */
function stepScan(st: ToolsState, eid: number): void {
  const ctx = st.ctx
  const player = ctx.player
  const scan = st.scan
  const nav = player.nav
  const holding =
    ctx.input.scanHeld && player.mode !== TOOL.combat && isScannable(nav.kind) && scanTargetInRange(st, nav.kind, nav.index)
  if (!holding) {
    player.scanProgress = 0
    scan.progress = 0
    st.scanLatched = false
    return
  }
  if (st.scanLatched) return
  const progress = player.scanProgress + SIM_DT / ctx.data.mining.scan.holdSeconds
  if (progress < SCAN_DONE) {
    player.scanProgress = progress
    scan.progress = progress
    return
  }
  const mk = ctx.mirror.tools.scannerMk
  const ok = nav.kind === NAV.wreck ? st.deps.salvage.scan(nav.index, mk, scan.wreck) : st.deps.rocks.scan(nav.kind, nav.index, mk, scan.rock)
  if (!ok) {
    player.scanProgress = 0
    scan.progress = 0
    return
  }
  player.scanProgress = 1
  scan.progress = 1
  scan.complete = true
  scan.kind = nav.kind
  scan.index = nav.index
  setNav(player.scanned, nav.kind, nav.index)
  st.scanLatched = true
  emitSpaceEvent(ctx.events, SEV.scanComplete, eid, nav.kind, nav.index, 0, 0, 0, 0, 0, 0)
}

/** The player's tool intent for this tick; outside Combat mode the M1 triggers are zeroed (no gun or torpedo fires). */
function writeToolControl(st: ToolsState, eid: number): void {
  const player = st.ctx.player
  ToolControl.mode[eid] = player.mode
  ToolControl.fire[eid] = Control.firePrimary[eid]
  ToolControl.alt[eid] = Control.fireSecondary[eid]
  ToolControl.power[eid] = player.power
  if (player.mode !== TOOL.combat) {
    Control.firePrimary[eid] = 0
    Control.fireSecondary[eid] = 0
  }
}

function clearToolControl(eid: number): void {
  ToolControl.fire[eid] = 0
  ToolControl.alt[eid] = 0
}

/** Laser power: clamps the target to the fitted laser's max and eases `power` toward it (`fracture.powerSmoothing`). */
function stepPower(st: ToolsState): void {
  const player = st.ctx.player
  const max = st.ctx.mirror.tools.laserMaxPower
  if (player.powerTarget > max) player.powerTarget = max
  if (player.powerTarget < 0) player.powerTarget = 0
  const k = 1 - Math.exp(-st.ctx.data.mining.fracture.powerSmoothing * SIM_DT)
  player.power += (player.powerTarget - player.power) * k
}

/** The control stage (plan §2.3 row 1, before `docking.control`). */
function toolsControl(st: ToolsState, sim: Sim): void {
  const ctx = st.ctx
  const input = ctx.input
  const player = ctx.player
  const eid = sim.playerEid
  const alive = eid >= 0 && Life.alive[eid] === 1
  st.controlEid = eid
  st.aliveAtControl = alive

  trackLeft(st, eid)
  if (alive) {
    loadRay(eid)
    if (input.cycleMode) applyMode(st, (player.mode + 1) % TOOL_COUNT, eid)
    if (input.modeRequest >= 0) applyMode(st, input.modeRequest, eid)
    if (input.powerSteps !== 0 && player.mode === TOOL.mining) {
      player.powerTarget += input.powerSteps * ctx.data.mining.fracture.powerStep
    }
    if (input.targetPressed) pickTarget(st)
    if (input.dockPressed) pressDock(st)
  }
  clearToolInputEdges(input)
  stepPower(st)
  if (!alive) {
    if (eid >= 0) clearToolControl(eid)
    return
  }
  syncNav(st, eid)
  writeToolControl(st, eid)
  stepScan(st, eid)
}

/** The deaths stage (plan §2.3 row 6, last): the player died this tick → `playerDied` and auto-dock off. */
function toolsDeaths(st: ToolsState, sim: Sim): void {
  const eid = st.controlEid
  const ring = sim.events
  const from = st.killScanFrom
  st.killScanFrom = ring.head
  if (!st.aliveAtControl || eid < 0 || Life.alive[eid] === 1) return
  st.aliveAtControl = false
  let killer = -1
  let x = Pos64.x[eid]
  let y = Pos64.y[eid]
  let z = Pos64.z[eid]
  for (let i = firstReadable(ring, from); i < ring.head; i++) {
    const s = eventSlot(i)
    if (ring.type[s] !== EV.kill || ring.eid[s] !== eid) continue
    killer = ring.other[s]
    x = ring.x[s]
    y = ring.y[s]
    z = ring.z[s]
  }
  emitSpaceEvent(st.ctx.events, SEV.playerDied, killer, eid, 0, 0, x, y, z, 0, 0)
  // Auto-dock stops with its autodockCancel cue; the clearance itself is released by docking.end later this
  // tick (M2-14 drops the grant of a dead player), so the frozen DockingRuntime needs no extra member.
  if (st.docking.readout.phase === 'autodock') st.docking.cancelAutodock()
  clearToolControl(eid)
  st.ctx.player.scanProgress = 0
  st.scan.progress = 0
  st.scanLatched = false
}

export function createToolsRuntime(ctx: SpaceContext, deps: ToolsDeps): ToolsRuntime {
  const st: ToolsState = {
    ctx,
    deps,
    docking: deps.docking,
    scan: createScanReadout(),
    lastNav: createNavTarget(),
    scanLatched: false,
    controlEid: -1,
    aliveAtControl: false,
    killScanFrom: ctx.sim.events.head,
    evFrom: ctx.events.head,
    leftId: '',
  }
  setNav(st.lastNav, ctx.player.nav.kind, ctx.player.nav.index)
  return {
    player: ctx.player,
    scan: st.scan,
    setMode(mode: ToolMode): void {
      applyMode(st, mode, ctx.sim.playerEid)
    },
    control: (sim: Sim): void => toolsControl(st, sim),
    deaths: (sim: Sim): void => toolsDeaths(st, sim),
  }
}
